import { Component, OnInit, ViewChild } from '@angular/core';
import { CompanyService } from 'app/company/services/company.service';
import { SharedService } from 'app/shared/services/shared.service';
import { User } from 'app/shared/models/User';
import { Routes, ActivatedRoute, Router } from '@angular/router';
import { IHeaderTitle } from 'app/shared/models/header-title';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { CompanyDetailsModel } from 'app/company/models/company-details-model';
import { AlertMessageComponent } from 'app/shared/components/alert-message/alert-message.component';
import { Location, DatePipe } from '@angular/common';
import { CompanyTypeModel } from 'app/company/models/company-type-model';
import { CompanyListModel } from 'app/company/models/company-list.model';
import { CompanyUserListModel } from 'app/company/models/company-user-list-model';
import { AggregatorModel } from 'app/company/models/aggregator-model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CompanyAuditTemplateDetailsModel } from 'app/company/models/company-audit-template-details.model';

@Component({
    templateUrl: './company-details.component.html',
    styleUrls: ['./company-details.component.css']
})
export class CompanyDetailsComponent implements OnInit {
    user: User;
    isAdmin: boolean = false;
    companyDetailsModel: CompanyDetailsModel;
    companyTypesList: CompanyTypeModel[];
    companiesList: CompanyListModel[];
    aggregatorsList: AggregatorModel[];
    errors: string[];
    headerTitle: IHeaderTitle;
    submitAttempt = false;
    @ViewChild(AlertMessageComponent, { static: false }) alertMessage: AlertMessageComponent;
    redirectRoutes: Routes;
    companyDetailsFormGroup: FormGroup;
    isEditMode = false;
    companyId: string;
    companyTypeString: string;
    parentString: string;
    aggregatorString: string;
    users: CompanyUserListModel[];
    selectedCompanyId: string;
    companyAuditTemplates: CompanyAuditTemplateDetailsModel[];

    constructor(
        private companyService: CompanyService,
        private sharedService: SharedService,
        private activatedRoute: ActivatedRoute,
        private fb: FormBuilder,
		private location: Location,
        private router: Router,
        private datePipe: DatePipe,
        private modalService: NgbModal
    ) { }

    ngOnInit() {

        this.sharedService.currentUser.subscribe(user => {
            this.user = user;
            this.isAdmin = this.user.isAdmin;
        });

        this.load();
    }

    load(): void {
        this.companyId = this.activatedRoute.snapshot.params['id'];

        this.companyService.get(this.companyId).subscribe(companyInfo => {
            companyInfo.aclExpiry = companyInfo.aclExpiry ? new Date(companyInfo.aclExpiry) : null;
            companyInfo.piExpiry = companyInfo.piExpiry ? new Date(companyInfo.piExpiry) : null;
            companyInfo.afcaExpiry = companyInfo.afcaExpiry ? new Date(companyInfo.afcaExpiry) : null;
            this.companyDetailsModel = companyInfo;
            this.initializeForm(companyInfo);
            if (this.isAdmin) {
                this.headerTitle = {
                    title: companyInfo.legalName,
                    breadcrumbs: [{ title: 'Companies', url: ['/company'] }]
                };
            } else {
                this.headerTitle = {
                    title: companyInfo.legalName
                };
            }
            this.companyService.getCompanyTypes().subscribe(
                result => {
                    this.companyTypesList = result;
                    this.companyTypeString = result.filter(c => c.id === companyInfo.companyTypeId)[0].description;
                },
                error => {
                    this.errors = error.errorList;
                }
            );
            this.companyService.getAll().subscribe(
                result => {
                    this.companiesList = result;
                    if (companyInfo.parentId === null) {
                        this.parentString = "None";
                    } else {
                        this.parentString = result.filter(c => c.id === companyInfo.parentId)[0].legalName;
                    }
                },
                error => {
                    this.errors = error.errorList;
                }
            );
            this.companyService.getAggregators().subscribe(
                result => {
                    this.aggregatorsList = result;
                    if (companyInfo.aggregatorId === null) {
                        this.aggregatorString = "None";
                    } else {
                        this.aggregatorString = result.filter(a => a.id === companyInfo.aggregatorId)[0].name;
                    }
                },
                error => {
                    this.errors = error.errorList;
                }
            );
            this.companyService.getUsers(this.companyId).subscribe(
                users => {
                    this.users = users
                },
                error => console.log(error)
            );
            this.companyService.getCompanyAuditTemplates(this.companyId).subscribe(
                result => {
                    this.companyAuditTemplates = result;
                },
                error => this.errors = error.errorList
            );
        });


    }

    initializeForm(model: CompanyDetailsModel): void {
        this.companyDetailsFormGroup = this.fb.group(
            {
                id: [this.companyId],
                legalName: [model.legalName, Validators.required],
                tradingName: [model.tradingName],
                hasLogo: [model.hasLogo],
                companyTypeId: [model.companyTypeId, Validators.required],
                parentId: [model.parentId],
                parentFullAccess: [model.parentFullAccess],
                aggregatorId: [model.aggregatorId],
                aclNr: [model.aclNr, Validators.required],
                acrNr: [model.acrNr, this.requiredIfValidator(() => this.companyDetailsFormGroup.get('companyTypeId').value == 1)],
                abn: [model.abn],
                acn: [model.acn],
                aclExpiry: [model.aclExpiry],
                piExpiry: [model.piExpiry],
                afcaExpiry: [model.afcaExpiry],
                website: [model.website],
                mobileNr: [model.mobileNr],
                phoneNr: [model.phoneNr],
                faxNr: [model.faxNr],
                isActive: [model.isActive],
                usersNr: [model.usersNr, Validators.min(1)],
                notes: [model.notes]
            }
        );
        this.companyDetailsFormGroup.get('companyTypeId').valueChanges
            .subscribe(value => {
                this.companyDetailsFormGroup.get('acrNumber').updateValueAndValidity();
        });

    }

    requiredIfValidator(predicate) {
        return (formControl => {
            if (!formControl.parent) {
                return null;
            }
            if (predicate()) {
                return Validators.required(formControl);
            }
            return null;
        })
    }


    toggleEditMode(): void {
        this.isEditMode = !this.isEditMode;
    }

    updateCompany(): void {
        this.submitAttempt = true;
        if (this.companyDetailsFormGroup.valid) {
            if (this.companyDetailsFormGroup.get('usersNr').value > this.companyDetailsModel.usersNr && !this.user.isAdmin) {
                alert("Only Comply 365 Administrators can set the number of users per account");
            } else {
                var aclExpiry = this.companyDetailsFormGroup.get('aclExpiry').value;
                var piExpiry = this.companyDetailsFormGroup.get('piExpiry').value;
                var afcaExpiry = this.companyDetailsFormGroup.get('afcaExpiry').value;                

                const formValue = this.companyDetailsFormGroup.value;
                formValue.aclExpiry = aclExpiry ? new Date(new Date(aclExpiry).setHours(12)).toISOString() : null;
                formValue.piExpiry = piExpiry ? new Date(new Date(piExpiry).setHours(12)).toISOString() : null;
                formValue.afcaExpiry = afcaExpiry ? new Date(new Date(afcaExpiry).setHours(12)).toISOString() : null;

                this.companyService.update(formValue).subscribe(
                    response => {
                        this.alertMessage.showMessage();
                    },
                    error => {
                        this.errors = error.errorList;
                    }
                );
            }
        }
    }

    successClicked($event: any): void {
        this.router.navigate(['./company', this.companyId]);
        this.isEditMode = false;
    }

    onHide($event: any): void { }

    onBack(): void {
        this.location.back();
    }


    deleteCompany() {
        this.companyService.deleteCompany(this.selectedCompanyId).subscribe(
            result => this.router.navigate(['./dashboard']),
            error => console.log(error)
        );
    }

    openCannotDeleteCompany(content) {
        this.modalService.open(content);
    }

    get legalName() { return this.companyDetailsFormGroup.get('legalName'); }
    get aclNr() { return this.companyDetailsFormGroup.get('aclNr'); }
    get acrNr() { return this.companyDetailsFormGroup.get('acrNr'); }
    get abn() { return this.companyDetailsFormGroup.get('abn'); }
    get usersNr() { return this.companyDetailsFormGroup.get('usersNr'); }


}
